import { unref, watch, type Ref } from 'vue';
import type { SettingElementInterface } from '@/types/items';
import type { ApiDataProvider } from '@/api/ApiDataProvider';
import { useAppDataStore } from '@/stores/app-data';
import { usePredicateStore } from '@/stores/predicate-store';
import { useBrowserList, type BrowserListConfig } from '@/composables/useBrowserList';
import { useDoubleClick } from '@/composables/useDoubleClick';

interface ElementBrowserConfig extends Omit<BrowserListConfig<SettingElementInterface>, 'fetchFn'> {
    // The API alias of the elements the finder browses ('advantages', 'items'): the endpoint asked.
    elementType: string;
    provider: ApiDataProvider;
    closeOnAdd: Ref<boolean> | boolean;
    onAdd: (element: SettingElementInterface, modifiers: Record<string, string>) => void;
    isAdded: (element: SettingElementInterface) => boolean;
    canAdd: (element: SettingElementInterface) => boolean;
}

// A finder's browser: the browser list, and adding an element from it. The browser builds its own
// request from the finder's provider and element type, so a finder says only what it browses.
export function useElementBrowser(config: ElementBrowserConfig) {
    const appData = useAppDataStore();
    const predicateStore = usePredicateStore();

    const list = useBrowserList<SettingElementInterface>({
        ...config,
        // The session page size is read as each request is made, so a search, a refresh or a
        // reload after a size change all carry the size in force then. The provider adds it to a
        // first page only, and requests a page link as given (spec 2026-10-16).
        fetchFn: (url, predicates) => config.provider.fetchElements(
            appData.system?.uuid ?? '',
            config.elementType,
            url,
            predicates,
            predicateStore.pageSize,
        ),
    });

    // A new size is a new first page, so an open browser asks for it again. A closed one is left
    // alone and picks the size up when it next opens, the same open-only rule as a finder's
    // source-filter refetch (spec 2026-10-16).
    watch(() => predicateStore.pageSize, () => {
        if (list.isOpen.value) list.fetch();
    });

    const addElement = (element: SettingElementInterface, modifiers: Record<string, string> = {}) => {
        config.onAdd(element, modifiers);
        const shouldClose = unref(config.closeOnAdd);
        if (shouldClose) {
            list.close();
        }
    };

    // useDoubleClick wires single-click to select, double-click to add
    const { onClick: onElementClick } = useDoubleClick(
        (element: SettingElementInterface) => addElement(element),
        {
            onSingleClick: (element: SettingElementInterface) => {
                list.selectedElement.value = element;
            }
        }
    );

    const onAddClick = (element: SettingElementInterface, modifiers: Record<string, string> = {}) => {
        addElement(element, modifiers);
    };

    return { ...list, onElementClick, onAddClick };
}
